<script setup lang="ts">
import { currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import { onMounted, ref } from 'vue';
import { toast } from '@/ui-plus/sonner';
import { Button } from '@/components/ui/button';
import { Settings } from 'lucide-vue-next';
import type { ServiceFee } from '@lib/zod/serviceFee';
import { Input } from '@/components/ui/input';
import { Dialog, DialogTrigger, DialogScrollContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import CustomRateDialog from './StoreCustomRateDialog.vue';
import { zmgAdminOnlyCacheUpdateClient, zmgPublicCacheClient } from '@api-client/zmg_cache';
import DeliveryteamCustomRateDialog from './DeliveryteamCustomRateDialog.vue';

const defaultSupportPartnerFee = ref<ServiceFee>({
    ratePerOrder: 0,
    feePerOrder: 0
});

const saveFeeChanges = async () => {
    const { error } = await (await zmgAdminOnlyCacheUpdateClient.website.partner_info[':id'].$patch({
        param: { id: currentWebsiteId.value ?? '' },
        json: {
            defaultSupportPartnerFee: defaultSupportPartnerFee.value,
        }
    })).json();
    if (error) {
        console.error(error);
        toast.error('Failed to update fee settings');
    } else {
        toast.success('fee settings updated');
    }
}
const loadFeeSettings = async () => {
    const { data, error } = await (await zmgPublicCacheClient.website.partner_info[':id']['data.json'].$get({
        param: { id: currentWebsiteId.value ?? '' },
    })).json();
    if (error) {
        console.error(error);
        toast.error('Failed to load fee settings');
    } else if (data) {
        defaultSupportPartnerFee.value = data.defaultSupportPartnerFee || {
            ratePerOrder: 0,
            feePerOrder: 0
        };
    }
}


onMounted(() => {
    loadFeeSettings();
});

</script>

<template>
    <div class="space-y-4">
        <p class="text-muted-foreground">
            To earn as a support partner, please define your fees below.
            <br>
            it will be applied to all orders received by stores you provide support for.
            <br>
            as long as you provide a quality support to the store, you will earn a fee from their all orders from any
            website on this platform.
            <br>
            Any rate fees defined here will be added on top of cart totals.
            <br>
            Cap: your fee (rate % + fixed fee) cannot exceed 5% of the order subtotal.
            <br>
            Example: 100 subtotal → max 5.
        </p>
        <div class="grid gap-4">
            <div class="space-y-2">
                <label for="ratePerOrder"
                       class="text-sm font-medium">Rate per Order (%)</label>
                <Input id="ratePerOrder"
                       type="number"
                       min="0"
                       max="5"
                       step="0.01"
                       v-model="defaultSupportPartnerFee.ratePerOrder"
                       class="max-w-[200px]"
                       @input="defaultSupportPartnerFee.ratePerOrder = Number($event.target.value) || 0" />
                <p class="text-sm text-muted-foreground">
                    Recommended: 1-4%, all fee details are going to be shown to the buyers
                    transparently. Your visitors may not place orders, if you set a high rate.
                </p>
            </div>

            <div class="space-y-2">
                <label for="feePerOrder"
                       class="text-sm font-medium">Fixed Fee per Order</label>
                <Input id="feePerOrder"
                       type="number"
                       min="0"
                       step="0.01"
                       v-model="defaultSupportPartnerFee.feePerOrder"
                       class="max-w-[200px]"
                       @input="defaultSupportPartnerFee.feePerOrder = Number($event.target.value) || 0" />
                <!-- <p class="text-sm text-muted-foreground">Fixed fee per order</p> -->
            </div>

            <Button class="w-full"
                    @click="saveFeeChanges">
                Save
            </Button>

            <!-- Stores -->
            <p class="text-muted-foreground mt-4">
                <Dialog>
                    <DialogTrigger>
                        <Button variant="outline">
                            <Settings class="inline-block w-4 h-4" />
                            Apply custom rate for each store
                        </Button>
                    </DialogTrigger>
                    <DialogScrollContent>
                        <DialogHeader>
                            <DialogTitle>Custom Fee Settings for each store</DialogTitle>
                            <DialogDescription>
                                [You may apply a custom fee rate or fixed fee for each store
                                with the gear
                                <Settings class="inline-block w-4 h-4" /> buttons]
                            </DialogDescription>
                        </DialogHeader>
                        <CustomRateDialog />
                    </DialogScrollContent>
                </Dialog>
            </p>

            <p class="text-muted-foreground mt-4 hidden hidden-deliveryteam-component">
                <Dialog>
                    <DialogTrigger>
                        <Button variant="outline">
                            <Settings class="inline-block w-4 h-4" />
                            Apply custom rate for each deliveryteam
                        </Button>
                    </DialogTrigger>
                    <DialogScrollContent>
                        <DialogHeader>
                            <DialogTitle>Custom Fee Settings for each deliveryteam</DialogTitle>
                            <DialogDescription>
                                [You may apply a custom fee rate or fixed fee for each deliveryteam
                                with the gear
                                <Settings class="inline-block w-4 h-4" /> buttons]
                            </DialogDescription>
                        </DialogHeader>
                        <DeliveryteamCustomRateDialog />
                    </DialogScrollContent>
                </Dialog>
            </p>

        </div>
    </div>
</template>
